import { useCallback } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import AmountContainer from "app/pages/.shared/AmountContainer";
import AmountSign from "app/pages/.shared/AmountSign/AmountSign";
import Radio from "app/pages/.shared/form/Radio";
import PaymentCouponItem from "app/pages/Booking/Payment/PaymentCoupons/PaymentCouponItem/PaymentCouponItem";
import MoreDetails from "app/pages/.shared/MoreDetails";
import "../CreditRadio/CreditRadio.scss";
import { sendTagOnMoreDetailCreditNoteClick } from "app/utils/analytics";
import intervalToDuration from "date-fns/intervalToDuration";
import { SHOW_CREDIT_NOTES_EXPIRATION_MAX_YEAR } from "app/constants";

const CreditNoteRadio = ({ value, usableCreditNotes, unusableCreditNotes }) => {
	const handleOnClick = useCallback(() => {
		sendTagOnMoreDetailCreditNoteClick();
	}, []);

	const today = Date.now();

	return (
		<Radio
			showIcon={true}
			name="credit-note-radio"
			id="credit-note-radio"
			checked={true}
			data-testid={`coupon-credit-note-input`}
			body={
				<div className="credit-radio__body">
					<MoreDetails
						headerPosition={"bottom"}
						expandedLabel={<FormattedMessage id="product.page.less.details" />}
						collapsedLabel={<FormattedMessage id="product.page.more.details" />}
						onChange={handleOnClick}
					>
						{usableCreditNotes.map((usableCredit = {}) => {
							let showExpirationDate;

							if (usableCredit.maxUseDate) {
								const duration = intervalToDuration({
									start: today,
									end: usableCredit.maxUseDate,
								});

								showExpirationDate =
									duration.years <= SHOW_CREDIT_NOTES_EXPIRATION_MAX_YEAR;
							}

							return (
								<div className="credit-radio__coupon-item" key={usableCredit.title}>
									<PaymentCouponItem
										friendEmail={usableCredit.friendEmail}
										amount={usableCredit.amount}
										creationDate={usableCredit.creationDate}
										balance={usableCredit.balance}
										uses={usableCredit.uses}
										maxUseDate={showExpirationDate && usableCredit.maxUseDate}
										conditions={usableCredit.conditions}
										usageRule={
											<FormattedMessage id="payment.coupons.credit.note.usage.rule" />
										}
										title={usableCredit.title}
										usableAmount={usableCredit.usableAmount}
									/>
								</div>
							);
						})}

						{unusableCreditNotes.map((unusableCredit = {}) => {
							let showExpirationDate;

							if (unusableCredit.maxUseDate) {
								const duration = intervalToDuration({
									start: today,
									end: unusableCredit.maxUseDate,
								});

								showExpirationDate =
									duration.years <= SHOW_CREDIT_NOTES_EXPIRATION_MAX_YEAR;
							}

							return (
								<div
									className="credit-radio__coupon-item"
									key={unusableCredit.title}
								>
									<PaymentCouponItem
										disabled={true}
										friendEmail={unusableCredit.friendEmail}
										isApplicable={unusableCredit.isApplicable}
										amount={unusableCredit.amount}
										creationDate={unusableCredit.creationDate}
										balance={unusableCredit.balance}
										uses={unusableCredit.uses}
										maxUseDate={showExpirationDate && unusableCredit.maxUseDate}
										conditions={unusableCredit.conditions}
										title={unusableCredit.title}
										usableAmount={unusableCredit.usableAmount}
									/>
								</div>
							);
						})}
					</MoreDetails>
				</div>
			}
		>
			<div className="credit-radio__content">
				<div className="credit-radio__name">
					{usableCreditNotes.length === 1 ? (
						usableCreditNotes[0]?.title
					) : (
						<FormattedMessage id="payment.coupons.input.creditnotes.label" />
					)}
				</div>
				<div className="credit-radio__amount">
					<AmountContainer amount={value} prefix={<AmountSign sign="-" />} />
				</div>
			</div>
		</Radio>
	);
};

CreditNoteRadio.propTypes = {
	value: PropTypes.number,
	usableCreditNotes: PropTypes.array,
	unusableCreditNotes: PropTypes.array,
};

CreditNoteRadio.defaultProps = {
	usableCreditNotes: [],
	unusableCreditNotes: [],
};

export default CreditNoteRadio;
